/**
 * Every timestamp is stored in UTC (see api/src/db/pool.ts) but staff are all
 * in Perth, so every display of one is pinned to AWST explicitly — not the
 * viewing device's own timezone, which Intl/toLocaleString would otherwise
 * use and which could silently differ from person to person.
 */
const AWST = 'Australia/Perth';

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short', timeZone: AWST });
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-AU', { dateStyle: 'medium', timeZone: AWST });
}

/** The audit trail's field labels (see FIELD_LABELS in api/src/services/complaints.ts) that hold a timestamp. */
const TIMESTAMP_AUDIT_FIELDS = new Set(['Date closed', 'Acknowledgement', 'IDR response']);

/**
 * Audit log old_value/new_value are stored as raw text, not typed — format
 * them in AWST when the changed field is one of the timestamp fields above,
 * instead of showing the raw UTC string the value was serialised as.
 */
export function formatAuditValue(fieldChanged: string | null | undefined, value: string): string {
  return fieldChanged && TIMESTAMP_AUDIT_FIELDS.has(fieldChanged) ? formatDateTime(value) : value;
}

/**
 * AWST never observes daylight saving (Western Australia doesn't run DST),
 * so it's a fixed UTC+8 offset year-round — no Intl/timezone-database lookup
 * needed to convert to and from it, just plain arithmetic.
 */
const AWST_OFFSET_MINUTES = 8 * 60;

/**
 * Converts a native `<input type="datetime-local">` value (e.g.
 * "2026-10-16T14:30") into a UTC ISO string for the API, reading those
 * wall-clock numbers as AWST — not the browser's own timezone, which native
 * datetime-local inputs otherwise assume.
 */
export function awstLocalToUtcIso(localValue: string): string {
  const asIfUtc = Date.parse(`${localValue}:00Z`);
  return new Date(asIfUtc - AWST_OFFSET_MINUTES * 60_000).toISOString();
}

/**
 * Converts a stored UTC timestamp into a value a `datetime-local` input can
 * display, representing that moment's wall-clock time in AWST.
 */
export function utcIsoToAwstLocal(utcIso: string): string {
  const awstMillis = new Date(utcIso).getTime() + AWST_OFFSET_MINUTES * 60_000;
  return new Date(awstMillis).toISOString().slice(0, 16);
}
